import { Component, OnInit } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from './core/services/authentication.service';
import { NavbarService } from './shared/navbar/navbar.service';
import { AppLocaleStorageService } from './shared/util/local-storage.service';
import { RequestService } from './core/services/request.service';
import { VersionService } from './shared/version/version.service';
import { ThemeService } from './shared/theme/theme.service';
import { IUser } from './shared/auth/user';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import Gleap from '../../node_modules/gleap';

import * as WebFont from 'webfontloader';
import { Url } from './shared/util/url.class';
import { UrlService } from './core/services/url.service';

@Component({
  selector: 'app-root',
  template: `
    <router-outlet></router-outlet>
    <!-- First load: the 2027 splash as in the React app (AppLoading); index.html shows the same until Angular runs -->
    <div *ngIf="booting" class="tw:fixed tw:inset-0 tw:z-5001 tw:flex tw:items-center tw:justify-center tw:bg-sidebar">
      <div class="tw:flex tw:flex-col tw:items-center">
        <img src="assets/foodnotify-logo-2027.png" alt="FoodNotify" class="tw:h-[32px] tw:w-auto" />
        <ng-container *ngTemplateOutlet="spinner"></ng-container>
      </div>
    </div>
    <!-- Later navigations: the spinner over the content panel, the shell stays usable -->
    <ng-container *ngIf="showSpinner && !booting">
      <div class="spiner-overlayer"></div>
      <div
        class="tw:pointer-events-none tw:fixed tw:top-(--app-shell-top) tw:right-(--app-shell-right) tw:bottom-0 tw:left-(--app-shell-left) tw:z-5001 tw:flex tw:items-center tw:justify-center"
      >
        <ng-container *ngTemplateOutlet="spinner"></ng-container>
      </div>
    </ng-container>
    <ng-template #spinner>
      <svg
        class="tw:size-[20px] tw:animate-spin tw:text-primary"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle class="tw:opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path
          class="tw:opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>
    </ng-template>
  `
})
export class AppComponent implements OnInit {
  title = 'fn-client-webapp';
  showSpinner = false;
  /** Until the first page has loaded; a cancelled navigation (a guard's redirect) keeps the splash */
  booting = true;

  constructor(
    translateService: TranslateService,
    authenticationService: AuthenticationService,
    urlService: UrlService,
    private router: Router,
    requestService: RequestService,
    navbarService: NavbarService,
    alss: AppLocaleStorageService,
    versionService: VersionService,
    // Sets color-mode and data-ground on <html> from the last light / dark choice in this browser
    themeService: ThemeService
  ) {
    translateService.setDefaultLang('de');

    authenticationService.onAuthenticationSuccess().subscribe((user: IUser) => {
      /** TODO CHANGE TO supplier FROM localhost */
      if (location.host.includes('supplier') && router.url.startsWith('/supplier/login')) {
        router.navigate(['/supplier'], { replaceUrl: true });
      } else {
        if (router.url.startsWith('/login')) {
          router.navigate(['/'], { replaceUrl: true });
        }
        navbarService.loadNavbar();
        themeService.syncFromUser(user);
        if (user.lang !== alss.getItem('language')) {
          translateService.use(user.lang);
        }
      }

      Gleap.initialize('luccHMyE9jFDLSEHv9Rp6U23EhkTXIES');
      Gleap.identify(user.id.toString(), {
        email: user.email,
        name: [user.firstName, user.lastName].join(' ').trim()
      });
    });

    this.router.events.subscribe((e) => {
      if (e instanceof NavigationStart) {
        this.showSpinner = true;
      }
      if (e instanceof NavigationEnd) {
        this.showSpinner = false;
        this.booting = false;
      }
      if (e instanceof NavigationCancel) {
        this.showSpinner = false;
      }
      if (e instanceof NavigationError) {
        this.showSpinner = false;
        this.booting = false;
      }
    });

    translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      alss.setItem('language', event.lang);

      const user = authenticationService.getUser() as IUser;
      if (user && user.lang !== event.lang) {
        const url: Url = urlService.baseAppUrl;
        url.push('lang');
        url.put('lang', event.lang);

        requestService.get(url.serialize()).subscribe({
          next: () => {
            user.lang = event.lang;
            alss.setItem(`language`, event.lang);
          },
          error: (error) => {
            console.error(error);
          }
        });
      }
    });

    let language = alss.getItem('language');
    if (!language) {
      language = translateService.getBrowserCultureLang().split('-')[0]; // en-US.json
    }

    translateService.use(language);
  }
  ngOnInit(): void {
    WebFont.load({
      google: {
        families: [
          'ABeeZee',
          'Aref+Ruqaa',
          'Acme',
          'Allura',
          'Baloo',
          'Belgrano',
          'Butterfly+Kids',
          'Black Han Sans',
          'Cabin',
          'Chango',
          'Courgette',
          'Della+Respira',
          'Dosis',
          'Eater',
          'Exo',
          'Farsan',
          'Fenix',
          'Gabriela',
          'Galindo',
          'Gorditas',
          'Habibi',
          'Harmattan',
          'Jua',
          'Jura',
          'Kosugi',
          'Krub',
          'Lemon',
          'Lora',
          'Mali',
          'Marvel',
          'Muli',
          'Notable',
          'Oswald',
          'Petrona',
          'Pridi',
          'Raleway',
          'Rasa',
          'Scada',
          'Spirax',
          'Ultra',
          'Viga',
          'Yrsa',
          'Walter Turncoat'
        ]
      }
    });
  }
}
